/** Session-only cache for financial reads. No financial data is persisted to disk. */
type Entry = {
  response: Response;
  body: string;
  updatedAt: number;
  usedAt: number;
  load: () => Promise<Response>;
};
const entries = new Map<string, Entry>();
const pending = new Map<string, Promise<Response>>();
const listeners = new Set<(url: string) => void>();
const MAX_ENTRIES = 100;
const RETAIN_MS = 5 * 60_000;
let generation = 0;

export function subscribeQueryCache(listener: (url: string) => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function notify(url: string) {
  listeners.forEach(listener => listener(url));
}
export function clearQueryCache() {
  generation++;
  entries.clear();
  pending.clear();
}
export function invalidateQueryCache() {
  clearQueryCache();
  notify('*');
}
export function isCachedQuery(url: URL) {
  return /\/(asset-dashboard\/(?:networth-history|overview)\/|accounts\/(?:field-choices\/|[a-f0-9-]+\/(?:transactions\/|balance-history\/)?)?|transactions\/(?:categories\/|[a-f0-9-]+\/)?)$/.test(
    url.pathname
  );
}
function freshFor(url: string) {
  return url.includes('/field-choices/') ? RETAIN_MS : 30_000;
}

export async function cachedQuery(
  url: string,
  load: () => Promise<Response>,
  touch = true
): Promise<Response> {
  const now = Date.now();
  for (const [key, entry] of entries) {
    if (now - entry.usedAt > RETAIN_MS) entries.delete(key);
  }
  const cached = entries.get(url);
  const refresh = () => {
    const existing = pending.get(url);
    if (existing) return existing;
    const version = generation;
    const request = (async () => {
      const response = await load();
      if (version !== generation)
        throw new Error('Query invalidated; please retry.');
      if (response.ok) {
        const body = await response.clone().text();
        if (version !== generation)
          throw new Error('Query invalidated; please retry.');
        entries.delete(url);
        entries.set(url, {
          response: response.clone(),
          body,
          updatedAt: Date.now(),
          usedAt: cached?.usedAt ?? Date.now(),
          load,
        });
        while (entries.size > MAX_ENTRIES)
          entries.delete(entries.keys().next().value!);
        if (cached && cached.body !== body) notify(url);
      }
      return response;
    })().finally(() => {
      if (pending.get(url) === request) pending.delete(url);
    });
    pending.set(url, request);
    return request;
  };
  if (cached) {
    if (touch) cached.usedAt = now;
    if (now - cached.updatedAt >= freshFor(url))
      void refresh().catch(() => {
        /* Keep the last successful data on background failure. */
      });
    return cached.response.clone();
  }
  return (await refresh()).clone();
}

/** Revalidate recently viewed data after focus or an asynchronous bank update. */
export function refreshCachedQueries(transactionsLive = false) {
  if (document.visibilityState === 'hidden') return;
  for (const [url, entry] of entries) {
    if (transactionsLive && new URL(url).pathname.includes('/transactions/'))
      continue;
    if (
      Date.now() - entry.usedAt <= RETAIN_MS &&
      Date.now() - entry.updatedAt >= freshFor(url)
    ) {
      void cachedQuery(url, entry.load, false).catch(() => {});
    }
  }
}
